import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, MapPin, MessageCircle, CheckCircle, XCircle, Music, Inbox, User, CalendarDays, Image, Package, Plus, Trash2, Edit2 } from 'lucide-react';
import ChatWindow from '../components/ChatWindow';
import { toast } from 'sonner';

export default function ArtistDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [bookings, setBookings] = useState([]);
  const [artistProfile, setArtistProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  
  // Equipment state
  const [equipment, setEquipment] = useState([]);
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState({ name: '', description: '', price: '' });
  const [savingEquipment, setSavingEquipment] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, profileRes] = await Promise.all([
        apiClient.get('/artist/bookings'),
        apiClient.get('/artist/profile')
      ]);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setArtistProfile(profileRes.data);
      setEquipment(profileRes.data?.equipment || []);
      
      // Fetch conversations for all bookings (roster mode — no payment gating)
      const chatBookings = bookingsRes.data.filter(b => b.booking_status !== 'cancelled');
      const conversationsData = await Promise.all(
        chatBookings.map(async (booking) => {
          try {
            const messages = await apiClient.get(`/messages/booking/${booking.booking_id}`);
            return {
              booking,
              messages: messages.data,
              unreadCount: messages.data.filter(m => m.receiver_id === user.user_id && !m.read).length
            };
          } catch (err) {
            console.error(`Error fetching conversation for booking ${booking.booking_id}:`, err);
            return null;
          }
        })
      );
      setConversations(conversationsData.filter(c => c !== null));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    try {
      await apiClient.put(`/artist/bookings/${bookingId}/accept`);
      toast.success('Booking accepted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to accept booking');
    }
  };

  const handleDeclineBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to decline this booking?')) {
      return;
    }
    try {
      await apiClient.put(`/artist/bookings/${bookingId}/decline`);
      toast.success('Booking declined');
      fetchData();
    } catch (error) {
      toast.error('Failed to decline booking');
    }
  };

  // Equipment management functions
  const handleAddEquipment = async (e) => {
    e.preventDefault();
    if (!equipmentForm.name || !equipmentForm.price) {
      toast.error('Name and price are required');
      return;
    }

    setSavingEquipment(true);
    try {
      const response = await apiClient.post('/artist/equipment', {
        name: equipmentForm.name,
        description: equipmentForm.description,
        price: parseFloat(equipmentForm.price)
      });
      
      setEquipment([...equipment, response.data.equipment]);
      setEquipmentForm({ name: '', description: '', price: '' });
      setShowAddEquipment(false);
      toast.success('Equipment added successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add equipment');
    } finally {
      setSavingEquipment(false);
    }
  };

  const handleUpdateEquipment = async (e) => {
    e.preventDefault();
    if (!editingEquipment || !equipmentForm.name || !equipmentForm.price) {
      toast.error('Name and price are required');
      return;
    }

    setSavingEquipment(true);
    try {
      await apiClient.put(`/artist/equipment/${editingEquipment}`, {
        name: equipmentForm.name,
        description: equipmentForm.description,
        price: parseFloat(equipmentForm.price)
      });
      
      setEquipment(equipment.map(eq => 
        eq.equipment_id === editingEquipment 
          ? { ...eq, name: equipmentForm.name, description: equipmentForm.description, price: parseFloat(equipmentForm.price) }
          : eq
      ));
      setEquipmentForm({ name: '', description: '', price: '' });
      setEditingEquipment(null);
      toast.success('Equipment updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update equipment');
    } finally {
      setSavingEquipment(false);
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) {
      return;
    }

    try {
      await apiClient.delete(`/artist/equipment/${equipmentId}`);
      setEquipment(equipment.filter(eq => eq.equipment_id !== equipmentId));
      toast.success('Equipment deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete equipment');
    }
  };

  const startEditEquipment = (eq) => {
    setEditingEquipment(eq.equipment_id);
    setEquipmentForm({ name: eq.name, description: eq.description || '', price: eq.price.toString() });
    setShowAddEquipment(false);
  };

  const cancelEquipmentForm = () => {
    setShowAddEquipment(false);
    setEditingEquipment(null);
    setEquipmentForm({ name: '', description: '', price: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const pendingBookings = bookings.filter(b => b.booking_status === 'pending');
  const confirmedBookings = bookings.filter(b => b.booking_status === 'confirmed');

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="glass-default rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            {user?.picture && (
              <img src={user.picture} alt="Profile" className="w-16 h-16 rounded-full border-2 border-violet-500" />
            )}
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Artist Dashboard</h1>
              <p className="text-white/60">{user?.name}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/artist/calendar')}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500"
              >
                <CalendarDays size={18} className="mr-2" />
                Manage Availability
              </Button>
              <Button
                onClick={() => navigate('/artist/create')}
                variant="outline"
                className="border-white/20 hover:bg-white/10"
              >
                <Image size={18} className="mr-2" />
                Edit Profile
              </Button>
            </div>
            {artistProfile && (
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-white/60">Status</p>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    artistProfile.status === 'approved'
                      ? 'bg-green-500/20 text-green-300'
                      : 'bg-yellow-500/20 text-yellow-300'
                  }`}>
                    {artistProfile.status}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="glass-default border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm mb-1">Pending Requests</p>
                  <p className="text-3xl font-bold">{pendingBookings.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Calendar className="text-yellow-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-default border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm mb-1">Confirmed Bookings</p>
                  <p className="text-3xl font-bold">{confirmedBookings.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="text-green-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-default border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm mb-1">Total Bookings</p>
                  <p className="text-3xl font-bold text-violet-300" data-testid="total-bookings-stat">
                    {bookings.length}
                  </p>
                  <p className="text-xs text-white/40 mt-1">all-time booking requests</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <Music className="text-violet-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="glass-default border border-white/10 p-1">
            <TabsTrigger value="bookings" className="data-[state=active]:bg-violet-600">
              <Calendar size={18} className="mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-violet-600 relative">
              <MessageCircle size={18} className="mr-2" />
              Messages ({conversations.length})
              {conversations.filter(c => c.unreadCount > 0).length > 0 && (
                <span className="ml-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {conversations.filter(c => c.unreadCount > 0).length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            {/* Pending Requests */}
            {pendingBookings.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">Pending Booking Requests</h2>
            <div className="grid grid-cols-1 gap-4">
              {pendingBookings.map((booking) => (
                <Card
                  key={booking.booking_id}
                  data-testid={`pending-booking-${booking.booking_id}`}
                  className="glass-default border-white/10"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                          <Calendar className="text-violet-400" size={20} />
                          <div>
                            <p className="text-sm text-white/60">Event Date</p>
                            <p className="font-medium">{new Date(booking.event_date).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <MapPin className="text-violet-400" size={20} />
                          <div>
                            <p className="text-sm text-white/60">Location</p>
                            <p className="font-medium">
                              {booking.event_location?.address || (booking.is_local ? 'Local event' : 'Travel required')}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-white/60">Duration</p>
                          <p className="font-medium">{booking.event_duration} hours</p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          data-testid={`accept-booking-${booking.booking_id}`}
                          onClick={() => handleAcceptBooking(booking.booking_id)}
                          className="bg-green-600 hover:bg-green-500"
                        >
                          <CheckCircle size={18} className="mr-2" />
                          Accept
                        </Button>
                        <Button
                          data-testid={`decline-booking-${booking.booking_id}`}
                          onClick={() => handleDeclineBooking(booking.booking_id)}
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <XCircle size={18} className="mr-2" />
                          Decline
                        </Button>
                        <Button
                          onClick={() => setSelectedBooking(booking)}
                          variant="outline"
                          className="border-white/20 hover:bg-white/10"
                        >
                          <MessageCircle size={18} className="mr-2" />
                          Message
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Confirmed Bookings */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Confirmed Bookings</h2>
          {confirmedBookings.length === 0 ? (
            <Card className="glass-default border-white/10">
              <CardContent className="p-12 text-center">
                <Music size={48} className="mx-auto mb-4 text-white/40" />
                <p className="text-xl text-white/60">No confirmed bookings yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {confirmedBookings.map((booking) => (
                <Card
                  key={booking.booking_id}
                  data-testid={`confirmed-booking-${booking.booking_id}`}
                  className="glass-default border-white/10"
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300">
                        Confirmed
                      </span>
                      <span className="text-sm text-white/60">
                        {new Date(booking.event_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-white/60">
                        <Calendar size={16} />
                        <span>{booking.event_duration}h event</span>
                      </div>
                      {booking.event_location && (
                        <div className="flex items-center gap-2 text-white/60">
                          <MapPin size={16} />
                          <span className="truncate">{booking.event_location.address}</span>
                        </div>
                      )}
                    </div>
                    <div className="pt-4 border-t border-white/10">
                      <Button
                      onClick={() => setSelectedBooking(booking)}
                      className="w-full bg-violet-600 hover:bg-violet-500"
                      size="sm"
                    >
                      <MessageCircle size={16} className="mr-2" />
                      Message Client
                    </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="space-y-6">
              <h2 className="text-2xl font-bold">Messages</h2>
              {conversations.length === 0 ? (
                <Card className="glass-default border-white/10">
                  <CardContent className="p-12 text-center">
                    <Inbox size={48} className="mx-auto mb-4 text-white/40" />
                    <p className="text-xl text-white/60">No conversations yet</p>
                    <p className="text-white/40 mt-2">Messages will appear here when clients contact you</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {conversations.map((conversation) => (
                    <Card
                      key={conversation.booking.booking_id}
                      className="glass-default border-white/10 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => setSelectedBooking(conversation.booking)}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <User size={20} className="text-violet-400" />
                              <h3 className="font-semibold">{conversation.booking.client_name}</h3>
                              {conversation.unreadCount > 0 && (
                                <span className="w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                                  {conversation.unreadCount}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-white/60 mb-3">
                              <div className="flex items-center gap-1">
                                <Calendar size={16} />
                                {new Date(conversation.booking.event_date).toLocaleDateString()}
                              </div>
                            </div>
                            {conversation.messages.length > 0 && (
                              <p className="text-white/80 text-sm">
                                {conversation.messages[conversation.messages.length - 1].content.substring(0, 100)}
                                {conversation.messages[conversation.messages.length - 1].content.length > 100 ? '...' : ''}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            className="bg-violet-600 hover:bg-violet-500"
                          >
                            <MessageCircle size={16} className="mr-2" />
                            Open Chat
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Chat Window */}
        {selectedBooking && (
          <ChatWindow
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
          />
        )}
      </div>
    </div>
  );
}
